import { useMember, usePageData, usePopupData } from 'shared-ui/components/pages/PageWrapper'
import { useEffect, useRef } from 'react'
import { withRouter } from 'react-router-dom'
import { useWebsocket } from '~components/WebsocketEvents'
import useEvent from 'react-use-event-hook'
import AnalyticsEvents from 'shared-ui/utils/AnalyticsEvents'

function PageRetention({ location, history }) {
  const pageData = usePageData()
  const popupData = usePopupData()
  const member = useMember()
  const { socket } = useWebsocket()

  const pageVisitTimeout = useRef()
  const triggerRetentionTimeout = useRef()
  const retentionStartTime = useRef(null)
  const retentionElapsed = useRef(0)
  const retentionData = useRef(null)

  const pseudoRouteId = location.state?.pseudoRoute?._id

  const clearTimeouts = useEvent(() => {
    if (pageVisitTimeout.current) {
      clearTimeout(pageVisitTimeout.current)
      pageVisitTimeout.current = null
    }
    if (triggerRetentionTimeout.current) {
      clearTimeout(triggerRetentionTimeout.current)
      triggerRetentionTimeout.current = null
    }
  })

  const handleVisibilityChange = useEvent(() => {
    if (!retentionData.current) return
    if (!socket) return
    if (!member?._id) return

    const { routeId, triggerRetentionTime } = retentionData.current
    const memberId = member._id.toString()

    if (document.hidden) {
      if (triggerRetentionTimeout.current) {
        clearTimeout(triggerRetentionTimeout.current)
        triggerRetentionTimeout.current = null

        if (retentionStartTime.current) {
          retentionElapsed.current += Date.now() - retentionStartTime.current
          retentionStartTime.current = null
        }
      }
    } else {
      const remainingTime = triggerRetentionTime * 1000 - retentionElapsed.current

      if (remainingTime > 0) {
        retentionStartTime.current = Date.now()
        triggerRetentionTimeout.current = setTimeout(() => {
          socket.emit('pageRetention', {
            routeId,
            memberId,
            accountId: member.selectedAccount?._id,
          })
          retentionData.current = null
          retentionElapsed.current = 0
          retentionStartTime.current = null
        }, remainingTime)
      }
    }
  })

  useEffect(() => {
    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [handleVisibilityChange])

  const onRouteChanged = useEvent((routeId, data) => {
    if (!socket) return
    if (!member?._id) return
    if (!pageData) return

    clearTimeouts()
    retentionElapsed.current = 0
    retentionStartTime.current = null
    retentionData.current = null

    AnalyticsEvents.dataPush(member, data)

    const { props: { _id, elements, _link: { trigger } = {} } = {} } = data
    let triggerRetentionTime

    if (_id === routeId) {
      triggerRetentionTime = trigger?.retentionTime
    } else if (elements) {
      const childRoute = elements.find(({ _id }) => _id === pseudoRouteId)
      if (childRoute) {
        const { _link: { trigger } = {} } = childRoute
        triggerRetentionTime = trigger?.retentionTime
      }
    }

    const memberId = member._id.toString()

    if (triggerRetentionTime !== undefined) {
      if (!document.hidden) {
        retentionData.current = { routeId, triggerRetentionTime }
        retentionStartTime.current = Date.now()

        triggerRetentionTimeout.current = setTimeout(() => {
          socket.emit('pageRetention', {
            routeId,
            memberId,
            accountId: member.selectedAccount?._id,
          })
          retentionData.current = null
          retentionElapsed.current = 0
          retentionStartTime.current = null
        }, triggerRetentionTime * 1000)
      } else {
        retentionData.current = { routeId, triggerRetentionTime }
      }
    }

    pageVisitTimeout.current = setTimeout(
      () =>
        socket.emit('pageVisit', {
          routeId,
          memberId,
          section: pageData.props.id,
          accountId: member.selectedAccount?._id,
        }),
      2000,
    )
  })

  useEffect(() => {
    let routeId = pageData?.props?._id
    if (!routeId) return
    onRouteChanged(routeId, pageData)
  }, [onRouteChanged, pageData])

  useEffect(() => {
    if (!pseudoRouteId || history.action === 'POP') return
    onRouteChanged(pseudoRouteId, pageData)
  }, [onRouteChanged, pseudoRouteId, history.action, pageData])

  useEffect(() => {
    let popupId = popupData?.props?._id
    if (!popupId) return
    onRouteChanged(popupId, popupData)
  }, [onRouteChanged, popupData])

  useEffect(() => {
    return () => {
      clearTimeouts()
    }
  }, [clearTimeouts])

  return null
}

export default withRouter(PageRetention)
